// ===================================================================
// KAIXO - TtsButtons
// ===================================================================
// Componente único para los botones Text-to-Speech. Antes había 3
// bloques casi idénticos duplicados en ChatWindow.js (audio, vídeo,
// texto) que se desincronizaban al editarlos por separado — este
// centraliza la vista para que un cambio se aplique a los 3 casos.
//
// Recibe dos "pistas" de audio opcionales (p.ej. transcripción
// traducida + original) y los estados globales de TTS:
//   activeId / pausedId / handlers
//
// Cualquiera que no quiera mostrar un botón deja el texto vacío.
// ===================================================================

import React from 'react';
import { Icons } from '../Icons/Icons';

function TtsButton({ variant, onClick, icon, label }) {
    const className = variant === 'translated' ? 'tts-btn tts-btn-translated' : 'tts-btn';
    return (
        <button className={className} onClick={onClick}>
            <span className="tts-btn-icon">{icon}</span>
            <span className="tts-btn-label">{label}</span>
        </button>
    );
}

/**
 * Props:
 *   mid              — id del mensaje
 *   primary          — { text, lang }  (p. ej. el texto en idioma del lector)
 *   secondary        — { text, lang }  (p. ej. el texto original)
 *   voice            — 'male' | 'female'
 *   activeId         — id del mensaje que está reproduciendo actualmente
 *   pausedId         — id del mensaje pausado
 *   onPlay(mid, text, lang, voice)
 *   onPause(mid) / onResume(mid) / onStop(mid)
 *   labels           — { pause, resume, stop, translated, original }  (i18n)
 */
function TtsButtons({
    mid, primary, secondary, voice,
    activeId, pausedId,
    onPlay, onPause, onResume, onStop,
    labels = {}
}) {
    const isActive = activeId === mid;
    const isPaused = pausedId === mid;

    if (isActive) {
        return (
            <>
                <TtsButton
                    icon={<Icons.Pause size={14} />}
                    label={labels.pause || 'Pausa'}
                    onClick={() => onPause(mid)}
                />
                <button
                    className="tts-btn"
                    style={{ background: 'rgba(229,57,53,0.15)', color: '#e53935' }}
                    onClick={() => onStop(mid)}
                >
                    <span className="tts-btn-icon"><Icons.Stop size={14} color="#e53935" /></span>
                    <span className="tts-btn-label">{labels.stop || 'Detener'}</span>
                </button>
            </>
        );
    }

    if (isPaused) {
        return (
            <>
                <TtsButton
                    icon={<Icons.Play size={14} />}
                    label={labels.resume || 'Continuar'}
                    onClick={() => onResume(mid)}
                />
                <button
                    className="tts-btn"
                    style={{ background: 'rgba(229,57,53,0.15)', color: '#e53935' }}
                    onClick={() => onStop(mid)}
                >
                    <span className="tts-btn-icon"><Icons.Stop size={14} color="#e53935" /></span>
                    <span className="tts-btn-label">{labels.stop || 'Detener'}</span>
                </button>
            </>
        );
    }

    // Estado inicial: los dos posibles botones de reproducción
    return (
        <>
            {primary?.text && primary?.lang && (
                <TtsButton
                    icon={<Icons.Speaker size={14} />}
                    label={primary.lang || labels.translated || 'Traducción'}
                    onClick={() => onPlay(mid, primary.text, primary.lang, voice)}
                />
            )}
            {secondary?.text && secondary?.lang && secondary.lang !== primary?.lang && (
                <TtsButton
                    variant="translated"
                    icon={<Icons.Speaker size={14} />}
                    label={secondary.lang || labels.original || 'Original'}
                    onClick={() => onPlay(mid, secondary.text, secondary.lang, voice)}
                />
            )}
        </>
    );
}

export default TtsButtons;
